import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { isGroupActive } from './navigation'
import NavGroup from './NavGroup'

/**
 * The tablet navigation rail — VH-133
 *
 * Between 768px and 1279px a fixed 256px sidebar leaves roughly 512px of
 * content, which crowds the wide tables and the bento grids. So the third
 * layout: a 72px icon-only rail that expands over the content when the user
 * asks for it, rather than permanently taking a quarter of the screen.
 *
 * Expansion is driven by both hover and focus/tap. Hover alone would leave the
 * rail unusable on a touch tablet — the exact device this layout exists for —
 * and tap alone would feel broken with a mouse attached. Focus-within is what
 * makes it work for the keyboard.
 *
 * The expanded state renders the same NavGroup components as the desktop
 * sidebar, so groups, active marking and route-following behave identically
 * rather than being reimplemented at a second size.
 */
export default function IconRail({ navigation, pathname }) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  return (
    <div
      className="hidden md:flex xl:hidden fixed inset-y-0 left-0 z-30"
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      onFocus={() => setExpanded(true)}
      onBlur={(event) => {
        // Only collapse when focus leaves the rail entirely — moving between
        // its own links must not close it under the user.
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setExpanded(false)
        }
      }}
    >
      <div
        className={clsx(
          'flex flex-col overflow-y-auto overflow-x-hidden border-r border-outline-variant bg-surface-container pt-5 pb-4 transition-[width] duration-200 ease-out',
          expanded ? 'w-64 shadow-xl' : 'w-[72px]'
        )}
      >
        <div className="flex flex-shrink-0 items-center px-4">
          {expanded ? (
            <h1 className="text-2xl font-bold text-gradient truncate">VitaHub</h1>
          ) : (
            // The mark, not the wordmark: "V" is what fits in 72px without
            // being clipped mid-word.
            <span
              className="mx-auto text-2xl font-black text-primary-600"
              aria-hidden="true"
            >
              V
            </span>
          )}
        </div>

        <nav
          className="mt-8 flex-1 space-y-1 px-2"
          aria-label={t('nav.primary')}
        >
          {expanded
            ? navigation.map((item) => (
                <NavGroup key={item.key} item={item} pathname={pathname} />
              ))
            : navigation.map((item) => {
                const active = isGroupActive(item, pathname)
                return (
                  <Link
                    key={item.key}
                    to={item.href}
                    title={item.name}
                    aria-label={item.name}
                    aria-current={item.href === pathname ? 'page' : undefined}
                    className={clsx(
                      'flex h-11 items-center justify-center rounded-lg transition-colors',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                      active
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    )}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                  </Link>
                )
              })}
        </nav>
      </div>
    </div>
  )
}
